import { Alert, Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { dealDetailPath } from "../routes/paths";
import { formatError, isRlsDenial } from "../lib/errors";
import { formatMoney } from "../lib/money";
import type { DetailRouteContext } from "../hooks/useDetailRoute";

interface RelatedDealRow {
  id: string;
  name: string;
  amount: number | null;
  currency: string;
  stage: { name: string; stage_type: string } | null;
}

const FILTER_COL: Record<DetailRouteContext["kind"], string | null> = {
  contact: "primary_person_id",
  organization: "customer_organization_id",
  partner: "partner_organization_id",
  deal: null,
};

export function RelatedDealsList({ ctx }: { ctx: DetailRouteContext }) {
  const filterCol = FILTER_COL[ctx.kind];

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["related_deals", ctx.kind, ctx.id],
    enabled: !!filterCol,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("manna")
        .from("deals")
        .select("id, name, amount, currency, stage:pipeline_stages(name, stage_type)")
        .eq(filterCol!, ctx.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as RelatedDealRow[];
    },
  });

  if (!filterCol) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
        Related deals not tracked for this entity.
      </Typography>
    );
  }
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  // SPEC manna-web-ui §3.13: collapse RLS denials into the "no related
  // deals" empty state so we don't surface permission boundaries.
  if (isError && !isRlsDenial(error)) {
    return <Alert severity="error">{formatError(error)}</Alert>;
  }
  if (!data || data.length === 0 || isError) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
        No related deals.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      {data.map((deal) => {
        const amount =
          deal.amount != null
            ? formatMoney({ amount: deal.amount, currency: deal.currency || "USD" })
            : null;
        const stageColor: "default" | "success" | "error" =
          deal.stage?.stage_type === "won"
            ? "success"
            : deal.stage?.stage_type === "lost"
              ? "error"
              : "default";
        return (
          <Box key={deal.id}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              component={RouterLink}
              to={dealDetailPath(deal.id)}
              sx={{
                textDecoration: "none",
                color: "inherit",
                py: 0.5,
                "&:hover .deal-name": { textDecoration: "underline" },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap className="deal-name">
                  {deal.name}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                  {deal.stage?.name && (
                    <Chip
                      size="small"
                      label={deal.stage.name}
                      color={stageColor}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </Stack>
              </Box>
              {amount && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {amount}
                </Typography>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
